import { useContext, useEffect, useMemo, useState } from "react";
import { useRouter } from "next/router";
import useSWR from "swr";
import { BottomBar, Menu, Status, Tabs } from "~components";
import { BaseContext, UserContext } from "~context";
import { getError, sdk, showToast } from "~lib";
import {
  Information,
  NonLoggedInRenewalBanner,
  RenewalBanner,
  Schedule,
} from "~components/memberships";
import { MembershipKind, MembershipQuery } from "~graphql/sdk";
import useCaptureClick from "~telemetry/traces/useCaptureClick";
import {
  AttributeNames,
  InteractionNames,
} from "~telemetry/enums/AttributeNames";
import { useOrganization } from "~hooks";
import { getEventVenueLocation } from "~lib/helpers/getEventVenueLocation";
import { useSeekaConverge } from "@seeka-labs/converge-react";
import { OptionalSeekaProvider } from "~lib/seeka";
import {
  Offering,
  IOfferingAction,
  OfferingHeader,
} from "~components/offering";
import { OfferingAccessCodeModal } from "~components/offering/offeringAccessCodeModal";

const getMembership = async (_: string, orgId: string, membershipId: string) =>
  sdk({ orgId }).membership({ id: membershipId });

const validateAccessCodeForMembership = async (
  _: string,
  orgId: string,
  code: string,
  membershipId: string
) =>
  sdk({ orgId }).validateAccessCodeForMembership({
    input: {
      membershipId,
      code,
    },
  });

const swrConfig = {
  shouldRetryOnError: false,
};

const hasMembershipToRenewFetcher = async (_: string, orgId: string) =>
  sdk({ orgId })
    .hasMembershipToRenew()
    .then((res) => res.myMembershipTickets);

const MembershipId = () => {
  const router = useRouter();
  const { organization, t } = useOrganization();

  const { data, error } = useSWR<MembershipQuery, Error>(
    organization?.id && router?.query?.membershipId
      ? ["memberships", organization?.id, router?.query?.membershipId]
      : null,
    getMembership,
    swrConfig
  );

  const isLoading = !data && !error;

  const isInactive = data?.membership?.isActive === false;

  const membership = data?.membership;

  return (
    <>
      <Menu />
      <Status error={error} loading={isLoading || isInactive}>
        <OptionalSeekaProvider seekaConfig={membership?.seekaConfig}>
          <Membership membership={membership} />
        </OptionalSeekaProvider>
      </Status>
      <BottomBar />
    </>
  );
};

function Membership({
  membership,
}: {
  membership: MembershipQuery["membership"];
}) {
  const [accessCodeModalIsOpen, setAccessCodeModalIsOpen] = useState(false);
  const [accessCodeError, setAccessCodeError] = useState<string | null>(null);
  const [isAccessCodeSubmitting, setIsAccessCodeSubmitting] = useState(false);
  const router = useRouter();
  const { organization, t } = useOrganization();
  const { isSinglePage } = useContext(BaseContext);
  const { isLoggedIn } = useContext(UserContext);
  const { data, error } = useSWR<MembershipQuery, Error>(
    organization?.id && router?.query?.membershipId
      ? ["memberships", organization?.id, router?.query?.membershipId]
      : null,
    getMembership,
    swrConfig
  );
  const converge = useSeekaConverge();

  const captureClick = useCaptureClick({
    forwardToNest: true,
    eventId: router?.query.eventId as string,
    pageName: "membership-details-page",
  });

  const isInactive = data?.membership?.isActive === false;

  useEffect(() => {
    if (isInactive) {
      void router.push("/memberships");
    }
  }, [isInactive]);

  const { data: myMemberships } = useSWR(
    organization?.id && organization?.showMembershipRenewal
      ? ["hasMembershipToRenew", organization?.id]
      : null,
    hasMembershipToRenewFetcher
  );

  const currentMembershipRenewalInformation = myMemberships?.find(
    (membership) =>
      membership.membershipType?.membership.id === router.query.membershipId
  );

  const hasMembershipToRenew =
    !currentMembershipRenewalInformation?.hasRenewedMembership &&
    !!currentMembershipRenewalInformation?.membershipType?.membership
      .renewalMembership?.id;

  if (error) {
    showToast(getError(error, "graphQL"), "error");
  }

  const isDigitalMembership =
    data?.membership?.membershipKind === MembershipKind.Digital;

  const showBanner =
    organization?.showMembershipRenewal && isLoggedIn && hasMembershipToRenew;

  const showNonLoggedInBanner =
    organization?.showMembershipRenewal && !isLoggedIn;

  const cartRecovery = router.query.cartRecovery;

  const accessCode = router.query.code as string | null;

  const membershipReservationLink = useMemo(() => {
    // Fallback to the original URL if the membership ID is not available
    if (!membership?.id) {
      return router.asPath;
    }

    const url = new URL(
      `/memberships/${membership?.id}/reservation`,
      window.location.origin
    );
    if (cartRecovery) {
      url.searchParams.set("cartRecovery", "true");
    }
    if (accessCode) {
      url.searchParams.set("code", accessCode);
    }
    return url.pathname + url.search;
  }, [cartRecovery, accessCode, membership?.id]);

  const eventVenues = membership?.events.map((event) => event?.venue);
  const venueLocations = getEventVenueLocation(eventVenues);

  const membershipName = t("membership_name");

  const digitalMembershipWarning = isDigitalMembership
    ? `Please note that this is a digital ${membershipName.toLowerCase()} which provides you online access only`
    : undefined;

  useEffect(() => {
    void converge?.track?.viewProduct({
      productIdentifier: membership?.id,
      productName: membership?.name,
    });
  }, []);

  const tabs = useMemo(
    () =>
      membership?.showSchedule
        ? [
            {
              id: "information",
              title: "Information",
              content: <Information {...membership} />,
            },
            {
              id: "schedule",
              title: "Schedule",
              content: <Schedule {...membership} />,
            },
          ]
        : [
            {
              id: "information",
              title: "Information",
              content: <Information {...membership} />,
            },
          ],
    [membership]
  );

  const primaryButtonLabel = useMemo(() => {
    // HACK: preexisting, left in
    if (organization.id === "2648ddb4-b50c-489f-a3eb-ae0249e3db3c") {
      return "Join now";
    }
    return isSinglePage ? "Buy Tickets" : `Buy ${membershipName}`;
  }, [isSinglePage, t, organization.id]);

  const actions: IOfferingAction[] = useMemo(() => {
    if (membership?.types.length <= 0) return [];
    const buttons: IOfferingAction[] = [
      {
        label: primaryButtonLabel,
        onClick: captureClick(
          () => void router.push(membershipReservationLink),
          InteractionNames.BUTTON_PRIMARY,
          {
            [AttributeNames.BUTTON_LABEL]: primaryButtonLabel,
          }
        ),
        variant: "primary",
      },
    ];
    if (membership?.hasAccessCodes) {
      buttons.push({
        label: "Have an access code?",
        variant: "secondary",
        onClick: () => setAccessCodeModalIsOpen(true),
      });
    }
    return buttons;
  }, [membership, setAccessCodeModalIsOpen]);

  const links = [
    {
      href: `/memberships/${membership?.id}/terms-and-conditions`,
      label: "Full Terms & Conditions",
    },
  ];

  const handleAccessCodeSubmit = async (code: string) => {
    setIsAccessCodeSubmitting(true);
    try {
      const result = await validateAccessCodeForMembership(
        "validateAccessCodeForMembership",
        organization?.id,
        code,
        membership?.id
      );
      const accessCode = result?.validateAccessCodeForMembership;
      setAccessCodeError(null);

      if (!accessCode?.id) {
        return;
      }

      sessionStorage.setItem("accessCodeId", accessCode.id);
      const url = new URL(membershipReservationLink, window.location.origin);
      url.searchParams.append("code", code);
      void router.push(url.pathname + url.search);
      setAccessCodeModalIsOpen(false);
    } catch (error) {
      setAccessCodeError(getError(error as Error, "graphQL"));
    } finally {
      setIsAccessCodeSubmitting(false);
    }
  };

  return (
    <>
      <Offering
        type="membership"
        title={membership?.name}
        description={membership?.description}
        supportInfo={organization?.supportInfo}
        accessibility={membership?.accessibility}
        startDate={membership?.startDate}
        endDate={membership?.endDate}
        venue={membership?.venue}
        location={venueLocations}
        coordinates={`${membership?.venue?.location?.lat},${membership?.venue?.location?.lng}`}
        backgroundImage={membership?.backgroundImage}
        bannerImage={membership?.banner}
        warning={digitalMembershipWarning}
        links={links}
      >
        {showBanner && <RenewalBanner button="secondary" mb={2} />}
        {showNonLoggedInBanner && <NonLoggedInRenewalBanner mb={2} />}
        <OfferingHeader
          title={membership?.name}
          location={venueLocations}
          actions={actions}
        />

        <Tabs items={tabs} />
      </Offering>
      <OfferingAccessCodeModal
        isOpen={accessCodeModalIsOpen}
        isLoading={isAccessCodeSubmitting}
        error={accessCodeError}
        onClose={() => setAccessCodeModalIsOpen(false)}
        onSubmit={handleAccessCodeSubmit}
        type="membership"
      />
    </>
  );
}

export default MembershipId;
